import { useEffect, useRef, useState, type RefObject } from 'react';
import type { DrawingLine } from '../model';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import type { ViewportTransform } from '@peruselab/structure-editor/domain/viewport';
import { createGridRenderer, type GridKind } from '@peruselab/structure-editor/rendering/canvasGrid';
import { createWebGlLineRenderer } from '@peruselab/structure-editor/rendering/webglLines';
import type { GridRenderer } from '@peruselab/structure-editor/rendering/canvasGrid';
import type { WebGlLineRenderer } from '@peruselab/structure-editor/rendering/webglLines';

interface PaperRendererOptions {
  readonly paperRef: RefObject<HTMLDivElement | null>;
  readonly gridCanvasRef: RefObject<HTMLCanvasElement | null>;
  readonly webGlCanvasRef: RefObject<HTMLCanvasElement | null>;
  readonly sizeRef: RefObject<PaperSize>;
  readonly lines: readonly DrawingLine[];
  readonly viewport: ViewportTransform;
  // #772 part 2 -- the grid to draw (spacing in drawing units), or null when hidden.
  readonly grid: { readonly kind: GridKind; readonly spacing: number } | null;
  readonly onWebGlAvailabilityChange: (available: boolean) => void;
  readonly onPaperSizeChange: ((size: PaperSize) => void) | undefined;
}

/**
 * Owns the imperative Canvas/WebGL lifecycle shared by the drawing paper. `sizeRef` is created
 * by the caller (not here) so it can also be shared with usePaperViewport, which needs the live
 * paper size for zoomBy/fitStructure without depending on this hook's `viewport` input.
 */
export function usePaperRenderers({
  paperRef,
  gridCanvasRef,
  webGlCanvasRef,
  sizeRef,
  lines,
  viewport,
  grid,
  onWebGlAvailabilityChange,
  onPaperSizeChange
}: PaperRendererOptions) {
  const viewportRef = useRef<ViewportTransform>(viewport);
  const linesRef = useRef<readonly DrawingLine[]>(lines);
  const gridRendererRef = useRef<GridRenderer | null>(null);
  const webGlRendererRef = useRef<WebGlLineRenderer | null>(null);
  const [paperSize, setPaperSize] = useState<PaperSize>({ width: 1, height: 1 });

  useEffect(() => {
    linesRef.current = lines;
  }, [lines]);
  useEffect(() => {
    viewportRef.current = viewport;
  }, [viewport]);

  useEffect(() => {
    const gridCanvas = gridCanvasRef.current;
    const webGlCanvas = webGlCanvasRef.current;
    const paper = paperRef.current;
    if (!gridCanvas || !webGlCanvas || !paper) return;

    gridRendererRef.current = createGridRenderer(gridCanvas);
    webGlRendererRef.current = createWebGlLineRenderer(webGlCanvas);
    onWebGlAvailabilityChange(webGlRendererRef.current.available);
    // #1007: SVG owns styled bond geometry. Raw graph centerlines would show through
    // double bonds and hashed wedges, so the WebGL surface must remain transparent.
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const nextSize = {
        width: Math.max(1, entry.contentRect.width),
        height: Math.max(1, entry.contentRect.height)
      };
      sizeRef.current = nextSize;
      setPaperSize(nextSize);
      onPaperSizeChange?.(nextSize);
      gridRendererRef.current?.resize(nextSize.width, nextSize.height);
      webGlRendererRef.current?.resize(nextSize.width, nextSize.height);
      webGlRendererRef.current?.render([]);
    });
    observer.observe(paper);
    return () => {
      observer.disconnect();
      gridRendererRef.current?.dispose();
      webGlRendererRef.current?.dispose();
      gridRendererRef.current = null;
      webGlRendererRef.current = null;
    };
  }, [gridCanvasRef, onPaperSizeChange, onWebGlAvailabilityChange, sizeRef, paperRef, webGlCanvasRef]);

  const gridKind = grid?.kind;
  const gridSpacing = grid?.spacing;
  useEffect(() => {
    gridRendererRef.current?.update(
      gridKind && gridSpacing
        ? { kind: gridKind, spacing: gridSpacing, zoom: viewport.zoom, panX: viewport.panX, panY: viewport.panY }
        : null
    );
  }, [gridKind, gridSpacing, viewport.zoom, viewport.panX, viewport.panY, paperSize]);

  return { linesRef, viewportRef, paperSize };
}
